import React, { useMemo, useState } from 'react';
import {
  useWindowDimensions,
  Linking,
  Image,
  View,
  Modal,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import RenderHTML from 'react-native-render-html';
import CCColors from '@/Utils/CCColors';
import { FontSizes } from '@/Utils/Dimensions';
import { formattedTextToHtml } from '@/Utils/CommonUtils';
import { MaterialIcons } from '@expo/vector-icons';
import CCText from '@/Atoms/CCText';

// onLinkPress: optional interceptor called before the default external open.
// Return true when it handled the link itself (e.g. an in-app navigate) to
// skip Linking.openURL; return false/undefined (or omit the prop entirely)
// to keep today's behaviour. Existing callers that don't pass it are
// unaffected -- see Screens/Clubs/components/ChatTab.tsx for a consumer.
const CCParsedMessage = ({ text, style, onLinkPress, bodyFormat = 'text' }) => {
  const { width } = useWindowDimensions();
  const fontSize = style?.fontSize ?? FontSizes[14];
  const baseUrl = 'https://crm.circlechess.com';
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Text/chunk mode: byte-identical to the pre-html-support tagsStyles.
  // formattedTextToHtml (markdown -> html for TEXT mode) can emit ul/ol/li,
  // so the html-mode-only additions below must NOT leak into this object --
  // RNRH's default ~1em list margins are what existing markdown messages
  // already render with, and changing that here would be a visual regression.
  const tagsStyles = {
    p: { marginTop: 4, marginBottom: 4, fontSize, lineHeight: fontSize * 1.1 },
    span: { fontSize, lineHeight: fontSize * 1.1 },
    strong: { fontWeight: '700' },
    em: { fontStyle: 'italic' },
    a: { color: CCColors.Level.Blue.Dark, textDecorationLine: 'underline' },
  } as const;

  const linkRenderersProps = {
    a: {
      onPress: (_, href) => {
        if (!href) {
          return;
        }
        if (!onLinkPress?.(href)) {
          Linking.openURL(href);
        }
      },
    },
  };

  // Html mode only: tagsStyles extended with h1-h4/img/ul-ol-li/blockquote/hr,
  // merged on top of the base set above. Memoized since it's only needed
  // (and only recomputed) when rendering an html body.
  const htmlTagsStyles = useMemo(
    () => ({
      p: { marginTop: 4, marginBottom: 4, fontSize, lineHeight: fontSize * 1.1 },
      span: { fontSize, lineHeight: fontSize * 1.1 },
      strong: { fontWeight: '700' },
      em: { fontStyle: 'italic' },
      a: { color: CCColors.Level.Blue.Dark, textDecorationLine: 'underline' },
      h1: { fontWeight: '700', fontSize: fontSize * 1.6, marginTop: 6, marginBottom: 6 },
      h2: { fontWeight: '700', fontSize: fontSize * 1.4, marginTop: 6, marginBottom: 6 },
      h3: { fontWeight: '700', fontSize: fontSize * 1.2, marginTop: 4, marginBottom: 4 },
      h4: { fontWeight: '700', fontSize: fontSize * 1.1, marginTop: 4, marginBottom: 4 },
      img: { maxWidth: '100%' },
      ul: { marginTop: 4, marginBottom: 4 },
      ol: { marginTop: 4, marginBottom: 4 },
      li: { fontSize, lineHeight: fontSize * 1.1 },
      blockquote: {
        borderLeftWidth: 3,
        borderLeftColor: CCColors.Border,
        paddingLeft: 8,
        marginVertical: 4,
        color: CCColors.TextColor.GreyText,
      },
      hr: {
        height: 1,
        backgroundColor: CCColors.BorderLight,
        marginVertical: 8,
      },
    }),
    [fontSize],
  );

  const htmlRenderersProps = useMemo(
    () => ({
      a: {
        onPress: (_, href) => {
          if (!href) {
            return;
          }
          if (!onLinkPress?.(href)) {
            Linking.openURL(href);
          }
        },
      },
      // maxWidth: '100%' on <img> is otherwise a no-op in RNRH v6 (it sizes
      // images from contentWidth in pixels unless percent widths are opted in).
      img: { enableExperimentalPercentWidth: true },
    }),
    [onLinkPress],
  );

  const htmlSource = useMemo(() => ({ html: text }), [text]);

  // Split text into text / image chunks. Skipped entirely for html bodies
  // (rendered directly below) so the markdown-chunking work never runs.
  const chunks = useMemo(() => {
    if (bodyFormat === 'html') {
      return [];
    }
    const IMAGE_EXTENSIONS = '(png|jpe?g|gif|webp|bmp|svg)';
    const regex = new RegExp(
      // image OR link
      `!\\[([^\\]]*)\\]\\(([^)]+)\\)|\\[([^\\]]+)\\]\\(([^)]+)\\)`,
      'gi'
    );

    let lastIndex = 0;
    const result: {
      type: 'text' | 'image' | 'link';
      content?: string;
      src?: string;
      alt?: string;
      href?: string;
      label?: string;
    }[] = [];

    let match;

    while ((match = regex.exec(text)) !== null) {
      const full = match[0];

      // text before match
      if (match.index > lastIndex) {
        result.push({
          type: 'text',
          content: text.slice(lastIndex, match.index),
        });
      }

      // IMAGE: ![alt](url)
      if (full.startsWith('!')) {
        const alt = match[1];
        const rawUrl = match[2];
        const isImage = new RegExp(`\\.(${IMAGE_EXTENSIONS})(\\?|$)`, 'i').test(rawUrl);

        if (isImage) {
          result.push({
            type: 'image',
            src: rawUrl.startsWith('http') ? rawUrl : `${baseUrl}${rawUrl}`,
            alt,
          });
        } else {
          // fallback: treat as link
          result.push({
            type: 'link',
            label: alt || rawUrl,
            href: rawUrl.startsWith('http') ? rawUrl : `${baseUrl}${rawUrl}`,
          });
        }
      }
      // LINK: [label](url)
      else {
        const label = match[3];
        const rawUrl = match[4];

        result.push({
          type: 'link',
          label,
          href: rawUrl.startsWith('http') ? rawUrl : `${baseUrl}${rawUrl}`,
        });
      }

      lastIndex = match.index + full.length;
    }

    // remaining text
    if (lastIndex < text.length) {
      result.push({
        type: 'text',
        content: text.slice(lastIndex),
      });
    }

    return result;
  }, [text, bodyFormat]);

  if (bodyFormat === 'html') {
    return (
      <View>
        <RenderHTML
          contentWidth={width}
          source={htmlSource}
          baseStyle={{
            fontSize,
            lineHeight: fontSize * 1.1,
            color: style?.color ?? CCColors?.TextColor?.Primary,
          }}
          defaultTextProps={{ selectable: true }}
          tagsStyles={htmlTagsStyles}
          renderersProps={htmlRenderersProps}
        />
      </View>
    );
  }

  return (
    <View>
      {chunks.map((chunk, idx) => {
        if (chunk.type === 'text') {
          const html = formattedTextToHtml(chunk.content ?? '');
          return (
            <RenderHTML
              key={idx}
              contentWidth={width}
              source={{ html }}
              baseStyle={{
                fontSize,
                lineHeight: fontSize * 1.1,
                color: style?.color ?? CCColors?.TextColor?.Primary,
              }}
              defaultTextProps={{ selectable: true }}
              tagsStyles={tagsStyles}
              renderersProps={linkRenderersProps}
            />
          );
        }

        if (chunk.type === 'image') {
          return (
            <TouchableOpacity key={idx} onPress={() => setPreviewImage(chunk.src)}>
              <Image
                source={{ uri: chunk.src }}
                style={{ width: 300, height: 200, resizeMode: 'contain', marginVertical: 8 }}
                accessibilityLabel={chunk.alt}
              />
            </TouchableOpacity>
          );
        }
        if (chunk.type === 'link') {
        return (
          <CCText
            key={idx}
            style={{
              color: CCColors.Level.Blue.Dark,
              textDecorationLine: 'underline',
              marginVertical: 4,
            }}
            onPress={() => {
              // onLinkPress handles specific in-app targets (e.g. Caissa
              // tournament links); fall through to a normal external open
              // otherwise.
              if (!onLinkPress?.(chunk.href!)) {
                Linking.openURL(chunk.href!);
              }
            }}>
            {chunk.label}
          </CCText>
        );
      }
      })}

      {/* Fullscreen preview modal */}
      {previewImage && (
        <Modal visible transparent onRequestClose={() => setPreviewImage(null)}>
          <View style={styles.bg}>
            <TouchableOpacity
              onPress={() => setPreviewImage(null)}
              style={styles.closeIcon}>
              <MaterialIcons name="close" size={32} color="white" />
            </TouchableOpacity>

            <Image
              source={{ uri: previewImage }}
              style={{ width: '90%', height: '90%' }}
              resizeMode="contain"
            />
          </View>
        </Modal>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  bg: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeIcon: {
    position: 'absolute',
    top: 40,
    right: 20,
    zIndex: 10,
  },
});

export default CCParsedMessage;
